import {Button} from '@common-web/components/ui/button';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Label} from '@common-web/components/ui/label';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {toast} from '@common-web/hooks/use-toast';
import {PageHeader} from '@common-web/components/page-header';
import {CheckCircle, Copy, Mail} from 'lucide-react';
import {CreateRegistrationResult} from '@internal/pages/users/new';

export function RegistrationSuccess({
  registrationData,
  onReset,
  onBack,
}: {
  registrationData: CreateRegistrationResult;
  onReset: () => void;
  onBack: () => void;
}) {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard',
      description: 'The access code has been copied to your clipboard',
    });
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Registration Created Successfully'
          description='The registration code has been generated and is ready to share'
          icon={<CheckCircle className='h-6 w-6' />}
        />
        <div className='px-8 py-6 max-w-3xl mx-auto w-full'>
          <Card className='border-gray-200 shadow-lg'>
            <CardHeader className='bg-emerald/5 border-b'>
              <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                <CheckCircle className='h-5 w-5 text-emerald' />
                Registration Details
              </CardTitle>
            </CardHeader>
            <CardContent className='p-6 space-y-6'>
              <div className='space-y-4'>
                <div>
                  <Label className='text-brand-gray'>Email</Label>
                  <div className='font-medium text-brand-navy'>{registrationData.email}</div>
                </div>
                <div>
                  <Label className='text-brand-gray'>Access Code</Label>
                  <div className='flex items-center gap-3'>
                    <div className='font-mono text-2xl bg-gray-100 px-4 py-2 rounded-sm flex-1'>
                      {registrationData.code}
                    </div>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => copyToClipboard(registrationData.code)}
                    >
                      <Copy className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className='text-brand-gray'>Expires At</Label>
                  <div className='font-medium text-brand-navy'>
                    {new Date(registrationData.expiresAt).toLocaleString()}
                  </div>
                </div>
              </div>
              <Alert>
                <Mail className='h-4 w-4' />
                <AlertTitle>Email Sent</AlertTitle>
                <AlertDescription>
                  The registration email has been sent to the user.
                </AlertDescription>
              </Alert>
              <div className='flex gap-3'>
                <Button
                  className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold'
                  onClick={onReset}
                >
                  Create Another
                </Button>
                <Button variant='outline' className='border-gray-200' onClick={onBack}>
                  Back to Users
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
